import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { klikBiasa } from '../utils/klikBiasa';
import {
  adminGetAllAffiliates,
  adminGetAffiliatesByIds,
  adminGetAllReferrals,
  adminGetPendingPayouts,
  adminGetPayoutHistory,
  adminApprovePayout,
  adminCompletePayout,
  adminRejectPayout,
  adminUpdateConfig,
  adminSetAffiliateStatus,
  adminGetAuditLogs,
  adminApproveCommission,
  adminApproveAllCommissions,
  adminSavePartnerGroup,
  adminDeletePartnerGroup,
  adminAssignPartnerGroup,
  adminGetPendingCommissionCount,
  getAffiliateConfig,
  getPartnerGroups,
} from '../services/affiliateService';
import { auth, db } from '../firebase';
import { terasNombor } from '../utils/nomborTelefon';
import { collection, query, getDocs, orderBy, limit } from 'firebase/firestore';

// Helpers untuk Last Sale column (lifted keluar dari component supaya pure)
const formatRelativeTime = (ts) => {
  if (!ts) return '—';
  const diff = Date.now() - ts;
  const day = 86400000;
  if (diff < day) return 'Hari ni';
  const days = Math.floor(diff / day);
  if (days < 30) return `${days}h lalu`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}bln lalu`;
  return `${Math.floor(days / 365)}thn lalu`;
};
const lastSaleColor = (ts) => {
  if (!ts) return 'text-slate-300 dark:text-slate-600';
  const diff = Date.now() - ts;
  const day = 86400000;
  if (diff < 7 * day) return 'text-emerald-600 dark:text-emerald-400';
  if (diff < 30 * day) return 'text-amber-500';
  return 'text-red-500';
};

/* ═══════════════════════════════════════════════════════════════
   MENU SUSUN — reka bentuk SAMA seperti butang susun /workspace
   (arahan user 2026-08-16): chip bergaris + menu tekan, tekan
   pilihan yang sedang aktif untuk terbalikkan arah.

   Kaedah susunnya sahaja yang berbeza — di sini ia metrik ejen
   (pendapatan, jualan, klik), bukan produk. Ia BERKONGSI state
   `sortBy`/`sortDir` dengan klik pada tajuk lajur, jadi dua
   kawalan itu tidak boleh menunjuk perkara berbeza.
   ═══════════════════════════════════════════════════════════════ */
const SUSUN_AFFILIATE = [
  { id: 'totalEarnings', label: 'Pendapatan', lalai: 'desc', tip: { asc: 'Rendah → Tinggi', desc: 'Tinggi → Rendah' } },
  { id: 'unpaidBalance', label: 'Baki belum bayar', lalai: 'desc', tip: { asc: 'Rendah → Tinggi', desc: 'Tinggi → Rendah' } },
  { id: 'paidCount', label: 'Jualan berbayar', lalai: 'desc', tip: { asc: 'Sedikit → Banyak', desc: 'Banyak → Sedikit' } },
  { id: 'totalReferrals', label: 'Rujukan', lalai: 'desc', tip: { asc: 'Sedikit → Banyak', desc: 'Banyak → Sedikit' } },
  { id: 'totalClicks', label: 'Klik', lalai: 'desc', tip: { asc: 'Sedikit → Banyak', desc: 'Banyak → Sedikit' } },
  { id: 'conversionRate', label: 'Kadar tukar', lalai: 'desc', tip: { asc: 'Rendah → Tinggi', desc: 'Tinggi → Rendah' } },
  { id: 'lastSaleAt', label: 'Jualan terakhir', lalai: 'desc', tip: { asc: 'Lama dahulu', desc: 'Terbaru dahulu' } },
  { id: 'name', label: 'Nama', lalai: 'asc', tip: { asc: 'A → Z', desc: 'Z → A' } },
];

const SortMenuAffiliate = ({ jenis, arah, onTukar }) => {
  const [buka, setBuka] = useState(false);
  const rujuk = useRef(null);

  useEffect(() => {
    if (!buka) return undefined;
    const luar = (e) => { if (rujuk.current && !rujuk.current.contains(e.target)) setBuka(false); };
    const esc = (e) => { if (e.key === 'Escape') setBuka(false); };
    document.addEventListener('mousedown', luar);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', luar);
      document.removeEventListener('keydown', esc);
    };
  }, [buka]);

  const kini = SUSUN_AFFILIATE.find((s) => s.id === jenis) || SUSUN_AFFILIATE[0];

  return (
    <div ref={rujuk} className="relative">
      <button
        type="button"
        onClick={() => setBuka((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={buka}
        className={`flex items-center gap-1.5 px-3 h-10 rounded-xl border text-xs font-bold whitespace-nowrap transition-colors ${buka
          ? 'bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200'
          : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-slate-400'}`}
      >
        <span className="material-symbols-outlined text-[16px] text-slate-400">swap_vert</span>
        {kini.label}
        <span className="material-symbols-outlined text-[14px] text-slate-400">
          {arah === 'asc' ? 'arrow_upward' : 'arrow_downward'}
        </span>
      </button>

      {buka && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-1 z-50 min-w-[230px] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl py-1 max-h-[320px] overflow-y-auto"
        >
          {SUSUN_AFFILIATE.map((s) => {
            const aktif = s.id === jenis;
            return (
              <button
                key={s.id}
                type="button"
                role="menuitem"
                onClick={() => { onTukar(s.id, aktif ? (arah === 'asc' ? 'desc' : 'asc') : s.lalai); setBuka(false); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              >
                <span className={`flex-1 text-[12px] font-bold ${aktif ? 'text-slate-800 dark:text-slate-100' : 'text-slate-500 dark:text-slate-400'}`}>
                  {s.label}
                </span>
                <span className="text-[10px] text-slate-400">{aktif ? s.tip[arah] : ''}</span>
                {aktif && (
                  <span className="material-symbols-outlined text-[14px] text-indigo-500">
                    {arah === 'asc' ? 'arrow_upward' : 'arrow_downward'}
                  </span>
                )}
              </button>
            );
          })}
          <p className="px-3 pt-1.5 pb-1 m-0 text-[10px] text-slate-400 border-t border-slate-100 dark:border-slate-800">
            Tekan pilihan yang sama untuk terbalikkan susunan.
          </p>
        </div>
      )}
    </div>
  );
};

/* Alamat guna perkataan English (`settings`), tetapi id tab dalaman sudah
   bernama `config` sejak awal dan ia dirujuk oleh blok render. Daripada
   menamakan semula 8 tempat, dua pembantu ini merapatkan kedua-duanya.
   ⛔ SATU takrifan sahaja — kalau UI mengira slug sendiri di tempat lain,
      cip tab akan menuding ke alamat yang tiada route dan jadi 404. */
const PETA_SLUG_TAB = { settings: 'config' };
const idTab = (slug) => PETA_SLUG_TAB[slug] || slug;
const slugTab = (id) => (id === 'config' ? 'settings' : id);

const AdminAffiliateView = ({ initialTab = 'overview' }) => {
  const [searchParams] = useSearchParams();
  /* Tab datang daripada ALAMAT (`/admin/affiliate/{tab}`) — lihat nota
     penuh dalam AdminView.jsx. Keadaan tempatan hanya cerminan alamat. */
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(idTab(initialTab));
  useEffect(() => { setActiveTab(idTab(initialTab)); }, [initialTab]);
  /* ⛔ Tukar tab = tukar ALAMAT. Jangan panggil setActiveTab terus —
     skrin akan bertukar tanpa alamat bertukar, dan bookmark jadi salah. */
  const bukaTab = (id, opsyen) => navigate(`/admin/affiliate/${slugTab(id)}`, opsyen);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [highlightedAffiliateId, setHighlightedAffiliateId] = useState(null);

  // Data states
  const [affiliates, setAffiliates] = useState([]);
  // Ejen yang dirujuk oleh payout/history TAPI tak masuk senarai adminGetAllAffiliates
  // (cth ejen lama tertolak keluar had) — di-resolve ikut ID supaya nama/AFR tetap muncul.
  const [payoutExtras, setPayoutExtras] = useState(new Map());
  const [payouts, setPayouts] = useState([]);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [groups, setGroups] = useState([]);

  // Commissions data
  const [allCommissions, setAllCommissions] = useState([]);
  const [pendingCommissionCount, setPendingCommissionCount] = useState(0);
  const [transactionMap, setTransactionMap] = useState(new Map());

  // Referrals data — semua user yang daftar via ref code (signup + converted)
  const [allReferrals, setAllReferrals] = useState([]);
  const [referralFilter, setReferralFilter] = useState('all'); // all | pending | converted
  const [referralJenisFilter, setReferralJenisFilter] = useState('all'); // all | new | renewal
  const [referralSearch, setReferralSearch] = useState('');
  // Map uid → user doc (untuk dapat whatsappNumber + name user yang baru daftar)
  const [userMap, setUserMap] = useState(new Map());

  // Search & filter
  const [searchQuery, setSearchQuery] = useState('');

  // Partner group editor modal
  const [editingGroup, setEditingGroup] = useState(null); // {id?, name, description, commissionRate, isDefault}
  const [savingGroup, setSavingGroup] = useState(false);

  // Config editing
  const [editableConfig, setEditableConfig] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);

  // Affiliate detail expand
  const [expandedAffiliate, setExpandedAffiliate] = useState(null);

  // Modals / Forms
  const [editingPayout, setEditingPayout] = useState(null);
  const [payoutNotes, setPayoutNotes] = useState('');

  // Sort + filter for Affiliates table
  const [sortBy, setSortBy] = useState('totalEarnings'); // default: top earners di atas
  const [sortDir, setSortDir] = useState('desc');
  const [activeOnly, setActiveOnly] = useState(false); // true = paidCount > 0 sahaja
  const [groupFilter, setGroupFilter] = useState('all'); // 'all' | groupId | 'none' — tapis jadual utama ikut partner group
  const [viewingGroupId, setViewingGroupId] = useState(null); // != null = papar listing basic ejen dalam group (tab Partner Groups)

  // Lookup: affiliateId → { name, email, refCode } untuk tunjuk nama ejen pada Payouts/History/Komisen (bukan UID)
  const affiliateMap = useMemo(() => {
    const m = new Map();
    affiliates.forEach(a => m.set(a.id, { name: a.name || '', email: a.email || '', refCode: a.refCode || '' }));
    // Gabung ejen tertinggal yang di-resolve ikut ID (payout/history) — jangan timpa yang sedia ada
    payoutExtras.forEach((v, k) => { if (!m.has(k)) m.set(k, v); });
    return m;
  }, [affiliates, payoutExtras]);

  // Resolve identiti ejen untuk 1 payout, dengan fallback berlapis:
  // 1) rekod ejen (map)  2) cop pada payout (affiliateName/…)  3) nama pemegang akaun bank
  const resolvePayoutAff = (p) => {
    const a = affiliateMap.get(p?.affiliateId) || {};
    return {
      name: a.name || p?.affiliateName || p?.bankDetails?.accountHolder || '',
      email: a.email || p?.affiliateEmail || '',
      refCode: a.refCode || p?.affiliateRefCode || '',
    };
  };

  // Load data on mount (PIN already verified by AdminPinGate wrapper)
  useEffect(() => {
    fetchData();
  }, []);

  // Handle ?ref=XXX param — auto-jump to Affiliates tab, filter by ref, expand + highlight the matching row
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (!ref || affiliates.length === 0) return;

    const match = affiliates.find(a =>
      (a.refCode || '').toLowerCase() === ref.toLowerCase() ||
      (a.customSlug || '').toLowerCase() === ref.toLowerCase()
    );

    /* Alamat yang membawa tab, jadi lencongan ini sekali gus MEMBUANG
       `?ref=` — tiada lagi keperluan setSearchParams untuk mengosongkannya. */
    bukaTab('affiliates', { replace: true });
    setSearchQuery(ref);

    if (match) {
      setExpandedAffiliate(match.id);
      setHighlightedAffiliateId(match.id);
      setTimeout(() => {
        const el = document.getElementById(`aff-row-${match.id}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      setTimeout(() => setHighlightedAffiliateId(null), 3000);
    } else {
      setToast({ type: 'error', message: `Tiada ejen dijumpai untuk kod "${ref}".` });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [affiliates, searchParams]);

  const fetchData = async () => {
    setLoading(true);
    const failed = [];

    // Resilient fetch — each query independent, partial failures don't block others
    const safe = async (label, fn, fallback = []) => {
      try { return await fn(); } catch (e) { failed.push(label); console.warn(`[Admin] Fetch failed — ${label}:`, e.message); return fallback; }
    };

    const [affs, pays, payHist, conf, grps, logs, pendingCount, refs] = await Promise.all([
      safe('Affiliates', () => adminGetAllAffiliates()),
      safe('Payouts', () => adminGetPendingPayouts()),
      safe('Payout History', () => adminGetPayoutHistory(500)),
      safe('Config', () => getAffiliateConfig(), null),
      safe('Partner Groups', () => getPartnerGroups()),
      safe('Audit Logs', () => adminGetAuditLogs(50)),
      safe('Pending Count', () => adminGetPendingCommissionCount(), 0),
      safe('Referrals', () => adminGetAllReferrals(3000))
    ]);

    setAffiliates(affs);
    setPayouts(pays);
    setPayoutHistory(payHist);

    // Resolve ejen yang dirujuk payout/history TAPI tak masuk senarai affs (tertolak keluar had) —
    // tarik ikut ID supaya nama/AFR/email tetap muncul, tanpa perlu load semua ejen.
    const known = new Set(affs.map(a => a.id));
    const referenced = [...new Set([...pays, ...payHist].map(p => p.affiliateId).filter(Boolean))];
    const missing = referenced.filter(id => !known.has(id));
    if (missing.length) {
      const extras = await safe('Payout Affiliates', () => adminGetAffiliatesByIds(missing));
      if (extras.length) {
        const em = new Map();
        extras.forEach(a => em.set(a.id, { name: a.name || '', email: a.email || '', refCode: a.refCode || '' }));
        setPayoutExtras(em);
      }
    }
    if (conf) { setEditableConfig({ commissionRate: conf.commissionRate || 0.25, cookieDuration: conf.cookieDuration || 30, minPayoutThreshold: conf.minPayoutThreshold || 5000 }); }
    setGroups(grps);
    setAuditLogs(logs);
    setPendingCommissionCount(typeof pendingCount === 'number' ? pendingCount : 0);
    setAllReferrals(refs);

    // Fetch commissions (all, for Commissions tab)
    const commsSnap = await safe('Commissions', () => getDocs(query(collection(db, 'commissions'), orderBy('createdAt', 'desc'), limit(2000))));
    if (commsSnap?.docs) { setAllCommissions(commsSnap.docs.map(d => ({ id: d.id, ...d.data() }))); }

    // Fetch transactions for customer info enrichment (keyed by referralId)
    const txnsSnap = await safe('Transactions', () => getDocs(query(collection(db, 'transactions'), orderBy('createdAt', 'desc'), limit(2000))));
    if (txnsSnap?.docs) {
      const map = new Map();
      txnsSnap.docs.forEach(d => {
        const data = d.data();
        if (data.referralId) map.set(data.referralId, data);
      });
      setTransactionMap(map);
    }

    // Fetch users (untuk enrichment WhatsApp + name pada tab Rujukan)
    const usersSnap = await safe('Users', () => getDocs(collection(db, 'users')));
    if (usersSnap?.docs) {
      const map = new Map();
      usersSnap.docs.forEach(d => map.set(d.id, { id: d.id, ...d.data() }));
      setUserMap(map);
    }

    if (failed.length > 0) {
      showToast(`Gagal muat: ${failed.join(', ')}. Firestore indexes mungkin belum dideploy.`, 'error');
    }
    setLoading(false);
  };

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const formatRM = (sen) => `RM ${(sen / 100).toFixed(2)}`;
  const formatDate = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('ms-MY', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const handleApprovePayout = async (payoutId) => {
    if (!confirm('Luluskan permintaan payout ini? STATUS: PROCESSING')) return;
    try {
      await adminApprovePayout(payoutId, auth.currentUser?.uid);
      showToast('Payout diluluskan dan sedang diproses!');
      fetchData();
    } catch (err) {
      showToast(err.message || 'Gagal meluluskan payout.', 'error');
    }
  };

  const handleCompletePayout = async (payout) => {
    if (!confirm(`Sahkan pembayaran ${formatRM(payout.amount)} telah dibayar ke ${payout.bankDetails?.bankName}? STATUS: COMPLETED`)) return;
    try {
      await adminCompletePayout(payout.id, payout.affiliateId, auth.currentUser?.uid);
      showToast('Payout selesai!');
      setEditingPayout(null);
      fetchData();
    } catch (err) {
      showToast(err.message || 'Gagal menyelesaikan payout.', 'error');
    }
  };

  const handleRejectPayout = async (payout) => {
    if (!payoutNotes) {
      showToast('Sila masukkan nota penolakan.', 'error');
      return;
    }
    if (!confirm('Tolak permintaan payout ini? STATUS: FAILED')) return;
    try {
      await adminRejectPayout(payout.id, auth.currentUser?.uid, payoutNotes);
      showToast('Payout ditolak!');
      setEditingPayout(null);
      setPayoutNotes('');
      fetchData();
    } catch (err) {
      showToast(err.message || 'Gagal menolak payout.', 'error');
    }
  };

  const handleApproveAccess = async (affiliate) => {
    if (!confirm(`Luluskan permohonan affiliate untuk ${affiliate.email}?`)) return;
    try {
      await adminSetAffiliateStatus(affiliate.id, 'active', auth.currentUser?.uid);
      showToast('Akses affiliate diluluskan.');
      fetchData();
    } catch (err) {
      showToast(err.message || 'Gagal meluluskan.', 'error');
    }
  };

  const handleRejectAccess = async (affiliate) => {
    if (!confirm(`Tolak permohonan affiliate untuk ${affiliate.email}?`)) return;
    try {
      await adminSetAffiliateStatus(affiliate.id, 'suspended', auth.currentUser?.uid);
      showToast('Permohonan ditolak.');
      fetchData();
    } catch (err) {
      showToast(err.message || 'Gagal menolak.', 'error');
    }
  };

  const handleApproveAllCommissions = async (affiliate) => {
    if (!confirm(`Luluskan SEMUA komisen pending untuk ${affiliate.name || affiliate.email}?`)) return;
    try {
      const { approved, skipped } = await adminApproveAllCommissions(affiliate.id, auth.currentUser?.uid);
      const msg = skipped > 0
        ? `${approved} komisen diluluskan. ${skipped} di-skip (status telah berubah).`
        : `${approved} komisen telah diluluskan.`;
      showToast(msg, skipped > 0 ? 'error' : 'success');
      fetchData();
    } catch (err) {
      showToast(err.message || 'Gagal meluluskan komisen.', 'error');
    }
  };

  const handleApproveOneCommission = async (commissionId) => {
    if (!confirm('Luluskan komisen ini?')) return;
    try {
      await adminApproveCommission(commissionId, auth.currentUser?.uid);
      showToast('Komisen diluluskan.');
      fetchData();
    } catch (err) {
      showToast(err.message || 'Gagal meluluskan.', 'error');
    }
  };

  const handleSaveGroup = async () => {
    if (!editingGroup) return;
    if (!editingGroup.name?.trim()) {
      showToast('Nama group wajib diisi.', 'error');
      return;
    }
    setSavingGroup(true);
    try {
      const { id, ...data } = editingGroup;
      await adminSavePartnerGroup(id || null, {
        name: data.name.trim(),
        description: data.description || '',
        commissionRate: parseFloat(data.commissionRate) || 0,
        tier2Rate: parseFloat(data.tier2Rate) || 0,   // kadar override tier-2 (0 = takda 2-tier)
        isDefault: !!data.isDefault
      });
      showToast(id ? 'Group dikemaskini.' : 'Group baru dicipta.');
      setEditingGroup(null);
      fetchData();
    } catch (err) {
      showToast(err.message || 'Gagal menyimpan group.', 'error');
    }
    setSavingGroup(false);
  };

  const handleDeleteGroup = async (group) => {
    if (!confirm(`Padam group "${group.name}"? Tindakan ini tidak boleh dipulihkan.`)) return;
    try {
      await adminDeletePartnerGroup(group.id);
      showToast('Group dipadam.');
      fetchData();
    } catch (err) {
      showToast(err.message || 'Gagal memadam group.', 'error');
    }
  };

  const handleSaveConfig = async () => {
    if (!editableConfig) return;
    setSavingConfig(true);
    try {
      await adminUpdateConfig({
        commissionRate: parseFloat(editableConfig.commissionRate),
        cookieDuration: parseInt(editableConfig.cookieDuration),
        minPayoutThreshold: parseInt(editableConfig.minPayoutThreshold)
      }, auth.currentUser?.uid);
      showToast('Tetapan berjaya disimpan.');
      fetchData();
    } catch (err) {
      showToast(err.message || 'Gagal menyimpan tetapan.', 'error');
    }
    setSavingConfig(false);
  };

  // Normalize MY-style phone number to wa.me format (strip non-digits, 0-prefix → 60)
  const toWaLink = (raw) => {
    if (!raw) return null;
    let digits = String(raw).replace(/\D/g, '');
    if (!digits) return null;
    if (digits.startsWith('0')) digits = '60' + digits.slice(1);
    return `https://wa.me/${digits}`;
  };

  const handleExportCSV = async (type) => {
    let csv = '';
    let filename = '';
    try {
      if (type === 'affiliates') {
        // Fresh fetch — avoid stale snapshot
        const fresh = await adminGetAllAffiliates();
        csv = 'Nama,Email,WhatsApp,Kod Rujukan,Rujukan,Pendapatan (sen),Baki (sen),Status\n';
        fresh.forEach(a => {
          csv += `"${a.name || ''}","${a.email || ''}","${a.whatsappNumber || ''}","${a.refCode || ''}",${a.totalReferrals || 0},${a.totalEarnings || 0},${a.unpaidBalance || 0},"${a.status || ''}"\n`;
        });
        filename = 'affiliates_export.csv';
      } else if (type === 'commissions') {
        const [freshSnap, txnSnap] = await Promise.all([
          getDocs(query(collection(db, 'commissions'), orderBy('createdAt', 'desc'), limit(2000))),
          getDocs(query(collection(db, 'transactions'), orderBy('createdAt', 'desc'), limit(2000)))
        ]);
        const txnByRef = new Map();
        txnSnap.docs.forEach(d => {
          const data = d.data();
          if (data.referralId) txnByRef.set(data.referralId, data);
        });
        csv = 'Affiliate ID,Customer Email,Customer Name,Jenis,Jumlah (sen),Kadar,Plan,Status,Tarikh\n';
        freshSnap.docs.forEach(d => {
          const c = d.data();
          const date = c.createdAt?.toDate ? c.createdAt.toDate().toISOString() : '';
          const t = txnByRef.get(c.referralId);
          // Resolver: c.purchaseType dulu, fallback ke referrals lookup
          const resolved = c.purchaseType || (c.referralId && purchaseTypeByReferralId.get(c.referralId)) || 'new';
          const jenis = resolved === 'renewal' ? 'Renewal' : 'Baru';
          csv += `"${c.affiliateId || ''}","${t?.customerEmail || ''}","${t?.customerName || ''}","${jenis}",${c.amount || 0},${c.rate || 0},"${c.plan || ''}","${c.status || ''}","${date}"\n`;
        });
        filename = 'commissions_export.csv';
      } else if (type === 'referrals') {
        csv = 'Nama User,User Baru (Email),WhatsApp User,Ejen Rujukan,Ejen Email,Ref Code,Status,Jenis,Tarikh Daftar\n';
        referralRows.forEach(r => {
          const date = r.createdAt?.toDate ? r.createdAt.toDate().toISOString() : (r.createdAt ? new Date(r.createdAt).toISOString() : '');
          const status = isReferralConverted(r) ? 'converted' : 'pending';
          const jenis = r.purchaseType === 'renewal' ? 'Renewal' : 'Baru';
          csv += `"${r.refereeName || ''}","${r.refereeEmail || ''}","${r.whatsappNumber || ''}","${r.referrerName || ''}","${r.referrerEmail || ''}","${r.refCode || ''}","${status}","${jenis}","${date}"\n`;
        });
        filename = 'referrals_export.csv';
      }
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      showToast(`${filename} dimuat turun.`);
    } catch (err) {
      showToast(err.message || 'Gagal eksport CSV.', 'error');
    }
  };

  const handleToggleAffiliateStatus = async (affiliate) => {
    if (affiliate.status === 'pending_access') {
      showToast('Affiliate ini masih pending. Guna butang Luluskan/Tolak.', 'error');
      return;
    }
    const newStatus = affiliate.status === 'active' ? 'suspended' : 'active';
    if (!confirm(`Tukar status affiliate ini kepada ${newStatus.toUpperCase()}?`)) return;
    try {
      await adminSetAffiliateStatus(affiliate.id, newStatus, auth.currentUser?.uid);
      showToast(`Status ditukar kepada ${newStatus}`);
      fetchData();
    } catch (err) {
      showToast(err.message || 'Gagal menukar status.', 'error');
    }
  };

  const pendingCommissions = allCommissions.filter(c => c.status === 'pending');

  // Referrals: anggap "converted" jika commissionAmount/amount > 0 atau status menunjukkan paid lifecycle
  const isReferralConverted = (r) => {
    const s = (r.status || '').toLowerCase();
    if (s === 'converted' || s === 'approved' || s === 'paid' || s === 'completed') return true;
    if ((r.commissionAmount || 0) > 0 || (r.amount || 0) > 0) return true;
    return false;
  };

  // Derive referral rows dari users.referredBy (canonical source — match Admin Dashboard "EJEN AFF" column).
  // Enrich dengan referral doc kalau wujud (status, type, commission). Kalau takde, fallback default.
  const referralRows = useMemo(() => {
    // When multiple referral docs exist for same refereeId (re-attribution history),
    // pick the doc with most authoritative status: converted > pending > transferred > voided.
    // Within same status, pick latest createdAt.
    const STATUS_RANK = { converted: 4, approved: 4, paid: 4, completed: 4, pending: 3, transferred: 2, voided: 1 };
    const refByRefereeId = new Map();
    allReferrals.forEach(r => {
      if (!r.refereeId) return;
      const existing = refByRefereeId.get(r.refereeId);
      if (!existing) { refByRefereeId.set(r.refereeId, r); return; }
      const rRank = STATUS_RANK[(r.status || '').toLowerCase()] ?? 0;
      const eRank = STATUS_RANK[(existing.status || '').toLowerCase()] ?? 0;
      if (rRank > eRank) { refByRefereeId.set(r.refereeId, r); return; }
      if (rRank === eRank) {
        const rt = r.createdAt?.toMillis ? r.createdAt.toMillis() : 0;
        const et = existing.createdAt?.toMillis ? existing.createdAt.toMillis() : 0;
        if (rt > et) refByRefereeId.set(r.refereeId, r);
      }
    });
    const affByRefCode = new Map();
    // Match refCode + customSlug + customSlugHistory — semua adalah valid identifier
    affiliates.forEach(a => {
      if (a.refCode) affByRefCode.set(a.refCode.toLowerCase(), a);
      if (a.customSlug) affByRefCode.set(a.customSlug.toLowerCase(), a);
      if (Array.isArray(a.customSlugHistory)) {
        a.customSlugHistory.forEach(s => { if (s) affByRefCode.set(String(s).toLowerCase(), a); });
      }
    });

    const rows = [];
    userMap.forEach(u => {
      if (!u.referredBy) return;
      const refDoc = refByRefereeId.get(u.id);
      const aff = affByRefCode.get(String(u.referredBy).toLowerCase()) || null;
      rows.push({
        id: refDoc?.id || `user-${u.id}`,
        refereeId: u.id,
        refereeEmail: u.email || refDoc?.refereeEmail || '',
        refereeName: u.name || '',
        whatsappNumber: u.whatsappNumber || '',
        refCode: u.referredBy,
        referrerId: refDoc?.referrerId || aff?.id || null,
        referrerEmail: refDoc?.referrerEmail || aff?.email || '',
        referrerName: aff?.name || '',
        status: refDoc?.status || 'pending',
        type: refDoc?.type || 'signup',
        purchaseType: refDoc?.purchaseType || null,   // tunjuk jenis Baru/Renewal (selaras dgn Komisen & Sejarah)
        commissionAmount: refDoc?.commissionAmount || 0,
        amount: refDoc?.amount || 0,
        createdAt: refDoc?.createdAt || u.createdAt || null,
        _hasReferralDoc: !!refDoc
      });
    });
    rows.sort((a, b) => {
      const at = a.createdAt?.toMillis ? a.createdAt.toMillis() : (a.createdAt ? new Date(a.createdAt).getTime() : 0);
      const bt = b.createdAt?.toMillis ? b.createdAt.toMillis() : (b.createdAt ? new Date(b.createdAt).getTime() : 0);
      return bt - at;
    });
    return rows;
  }, [userMap, allReferrals, affiliates]);

  const convertedReferralsCount = referralRows.filter(isReferralConverted).length;
  const pendingReferralsCount = referralRows.length - convertedReferralsCount;

  // BARU: lookup map purchaseType by referral.id — fallback untuk commission docs
  // lama yang takda field purchaseType (cipta sebelum Phase 23 deploy).
  const purchaseTypeByReferralId = useMemo(() => {
    const m = new Map();
    referralRows.forEach(r => {
      if (r.id) m.set(r.id, r.purchaseType || 'new');
    });
    return m;
  }, [referralRows]);

  // Resolver: dapat purchaseType untuk satu commission doc.
  // Cuba c.purchaseType dulu (data baru), fallback ke lookup via referralId (data lama).
  const resolveCommissionPurchaseType = (c) => {
    if (c.purchaseType) return c.purchaseType;
    if (c.referralId && purchaseTypeByReferralId.has(c.referralId)) {
      return purchaseTypeByReferralId.get(c.referralId);
    }
    return 'new'; // ultimate default
  };

  // Per-affiliate stats: paid client count + last sale timestamp (untuk Affiliates table)
  const affiliateStats = (() => {
    const map = new Map();
    referralRows.forEach(r => {
      if (!r.referrerId || !isReferralConverted(r)) return;
      const ts = r.createdAt?.toMillis ? r.createdAt.toMillis() : (r.createdAt ? new Date(r.createdAt).getTime() : 0);
      const cur = map.get(r.referrerId) || { paidCount: 0, lastSaleAt: 0 };
      cur.paidCount += 1;
      if (ts > cur.lastSaleAt) cur.lastSaleAt = ts;
      map.set(r.referrerId, cur);
    });
    return map;
  })();

  // Filtered + sorted affiliates: search → activeOnly → sort
  const filteredAffiliates = (() => {
    let arr = affiliates.filter(a => {
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      const qDigits = searchQuery.replace(/\D/g, ''); // angka sahaja — padanan no. WhatsApp abai-format
      const qTeras = terasNombor(qDigits);            // abai awalan 60 / +60 / 0 (data ejen kebanyakan "0…")
      const slugHistory = Array.isArray(a.customSlugHistory) ? a.customSlugHistory.join(' ').toLowerCase() : '';
      return (a.name || '').toLowerCase().includes(q) ||
             (a.email || '').toLowerCase().includes(q) ||
             (a.refCode || '').toLowerCase().includes(q) ||
             (a.customSlug || '').toLowerCase().includes(q) ||
             slugHistory.includes(q) ||
             (a.whatsappNumber || '').toLowerCase().includes(q) ||
             (qDigits.length >= 3 && (a.whatsappNumber || '').replace(/\D/g, '').includes(qDigits)) ||
             (qTeras.length >= 3 && terasNombor(a.whatsappNumber).includes(qTeras));
    });
    if (activeOnly) {
      arr = arr.filter(a => (affiliateStats.get(a.id)?.paidCount || 0) > 0);
    }
    if (groupFilter !== 'all') {
      arr = arr.filter(a => groupFilter === 'none' ? !a.partnerGroupId : a.partnerGroupId === groupFilter);
    }
    const getSortVal = (a) => {
      const s = affiliateStats.get(a.id) || { paidCount: 0, lastSaleAt: 0 };
      switch (sortBy) {
        case 'name': return (a.name || a.email || '').toLowerCase();
        case 'totalClicks': return a.totalClicks || 0;
        case 'totalReferrals': return a.totalReferrals || 0;
        case 'paidCount': return s.paidCount;
        case 'conversionRate': {
          const refs = a.totalReferrals || 0;
          return refs > 0 ? s.paidCount / refs : 0;
        }
        case 'lastSaleAt': return s.lastSaleAt;
        case 'totalEarnings': return a.totalEarnings || 0;
        case 'overrideEarnings': return a.overrideEarnings || 0;
        case 'unpaidBalance': return a.unpaidBalance || 0;
        default: return 0;
      }
    };
    return [...arr].sort((a, b) => {
      const va = getSortVal(a);
      const vb = getSortVal(b);
      const cmp = typeof va === 'string' ? va.localeCompare(vb) : va - vb;
      return sortDir === 'asc' ? cmp : -cmp;
    });
  })();

  /* Slug alamat -> id tab dalaman. Hanya 'config' yang berbeza; ia dinamakan
     begitu sejak awal dan menukarnya bermakna menyentuh 8 tempat render. */
  const tabs = [
    { id: 'overview', icon: 'dashboard', label: 'Overview' },
    { id: 'affiliates', icon: 'groups', label: `Affiliates (${affiliates.length})` },
    { id: 'referrals', icon: 'person_add', label: `Referrals (${referralRows.length})` },
    { id: 'commissions', icon: 'toll', label: `Commissions${pendingCommissionCount ? ` (${pendingCommissionCount})` : ''}` },
    { id: 'payouts', icon: 'payments', label: `Payouts (${payouts.length})` },
    { id: 'groups', icon: 'category', label: 'Partner Groups' },
    { id: 'config', icon: 'settings', label: 'Settings' },
    { id: 'logs', icon: 'receipt_long', label: 'Audit Logs' }
  ];

  // Loading state — pindah ke sini supaya semua hooks (useMemo) di atas selalu dipanggil dulu.
  // Letak sebelum early return = rules-of-hooks violation (yang punca crash localhost).
  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="w-8 h-8 border-4 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-slate-50 dark:bg-slate-950">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-6 right-6 z-50 flex items-center gap-2 px-4 py-2.5 rounded-xl shadow-lg text-sm font-bold animate-[slideIn_0.3s] ${toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-emerald-500 text-white'}`}>
          <span className="material-symbols-outlined text-[16px]">{toast.type === 'error' ? 'error' : 'check_circle'}</span>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="px-8 pt-6 pb-4 border-b border-slate-200 dark:border-slate-800">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-500/20">
            <span className="material-symbols-outlined text-white text-xl" style={{ fontVariationSettings: "'FILL' 1" }}>admin_panel_settings</span>
          </div>
          <div>
            <h1 className="text-xl font-black text-slate-800 dark:text-white">Admin: Pengurusan Affiliate</h1>
            <p className="text-xs text-slate-400">Urus affiliates, komisen, payout, dan tetapan program.</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mt-4">
          {tabs.map(tab => (
            /* Pautan sebenar supaya klik kanan / Ctrl+klik boleh buka tab baharu. */
            <a
              key={tab.id}
              href={`/admin/affiliate/${slugTab(tab.id)}`}
              onClick={(e) => { if (klikBiasa(e)) { e.preventDefault(); bukaTab(tab.id); } }}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition-all ${activeTab === tab.id
                ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800'
                : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                }`}
            >
              <span className="material-symbols-outlined text-[14px]" style={{ fontVariationSettings: activeTab === tab.id ? "'FILL' 1" : undefined }}>{tab.icon}</span>
              {tab.label}
            </a>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-8">
        {activeTab === 'overview' && (() => {
          // BARU: breakdown stats Baru vs Renewal — guna resolver (fallback lookup
          // via referrals untuk commission docs lama yang takda purchaseType field).
          // Tapis komisen 'voided' (jualan yang di-re-attribute / dibatalkan) supaya kad ni
          // tak kira komisen yang dah ditolak balik dari totalEarnings ejen — selaras dgn
          // kad Total Earnings Paid / Unpaid Balance yang guna agregat approved-only.
          const activeCommissions = allCommissions.filter(c => c.status !== 'voided');
          const newCommissions = activeCommissions.filter(c => resolveCommissionPurchaseType(c) !== 'renewal');
          const renewalCommissions = activeCommissions.filter(c => resolveCommissionPurchaseType(c) === 'renewal');
          const newTotal = newCommissions.reduce((sum, c) => sum + (c.amount || 0), 0);
          const renewalTotal = renewalCommissions.reduce((sum, c) => sum + (c.amount || 0), 0);
          const totalCommissionsPaid = newTotal + renewalTotal;
          const renewalRatePct = totalCommissionsPaid > 0
            ? ((renewalTotal / totalCommissionsPaid) * 100).toFixed(1)
            : '0.0';
          return (
          <div className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: 'Jumlah Affiliate', value: affiliates.length, icon: 'groups', color: 'indigo', gradient: 'from-indigo-400 to-purple-500' },
                { label: 'Pending Payout', value: payouts.length, icon: 'hourglass_empty', color: 'amber', gradient: 'from-amber-400 to-orange-500' },
                { label: 'Total Unpaid Balance', value: formatRM(affiliates.reduce((sum, a) => sum + (a.unpaidBalance || 0), 0)), icon: 'account_balance_wallet', color: 'rose', gradient: 'from-rose-400 to-red-500' },
                { label: 'Total Earnings Paid', value: formatRM(affiliates.reduce((sum, a) => sum + (a.totalEarnings || 0), 0) - affiliates.reduce((sum, a) => sum + (a.unpaidBalance || 0), 0)), icon: 'payments', color: 'emerald', gradient: 'from-emerald-400 to-teal-500' }
              ].map((stat, i) => (
                <div key={i} className="relative bg-white dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 overflow-hidden group hover:shadow-lg transition-all">
                  <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${stat.gradient} opacity-[0.06] rounded-bl-[60px] group-hover:opacity-[0.1] transition-opacity`} />
                  <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${stat.gradient} flex items-center justify-center mb-3 shadow-lg shadow-${stat.color}-500/20`}>
                    <span className="material-symbols-outlined text-white text-lg" style={{ fontVariationSettings: "'FILL' 1" }}>{stat.icon}</span>
                  </div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">{stat.label}</p>
                  <p className="text-2xl font-black text-slate-800 dark:text-white">{stat.value}</p>
                </div>
              ))}
            </div>

            {/* BARU: Breakdown Komisen Baru vs Renewal */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-white dark:bg-slate-900/50 border border-emerald-200 dark:border-emerald-800/50 rounded-2xl p-5 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-emerald-400 to-green-500 opacity-[0.08] rounded-bl-[60px]" />
                <p className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider mb-1">🆕 Komisen Baru</p>
                <p className="text-2xl font-black text-slate-800 dark:text-white">{formatRM(newTotal)}</p>
                <p className="text-[10px] text-slate-500 mt-1">{newCommissions.length} transaksi</p>
              </div>
              <div className="bg-white dark:bg-slate-900/50 border border-purple-200 dark:border-purple-800/50 rounded-2xl p-5 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-purple-400 to-fuchsia-500 opacity-[0.08] rounded-bl-[60px]" />
                <p className="text-[10px] font-bold text-purple-600 dark:text-purple-400 uppercase tracking-wider mb-1">🔄 Komisen Renewal</p>
                <p className="text-2xl font-black text-slate-800 dark:text-white">{formatRM(renewalTotal)}</p>
                <p className="text-[10px] text-slate-500 mt-1">{renewalCommissions.length} transaksi</p>
              </div>
              <div className="bg-white dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-slate-400 to-slate-600 opacity-[0.08] rounded-bl-[60px]" />
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">% Renewal Rate</p>
                <p className="text-2xl font-black text-slate-800 dark:text-white">{renewalRatePct}%</p>
                <p className="text-[10px] text-slate-500 mt-1">Daripada total komisen</p>
              </div>
            </div>

            {/* Quick Actions / Important alerts */}
            {affiliates.filter(a => a.status === 'pending_access').length > 0 && (
              <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-2xl p-5 flex items-start gap-4">
                <span className="material-symbols-outlined text-indigo-500 mt-0.5">how_to_reg</span>
                <div>
                  <h3 className="text-sm font-bold text-indigo-800 dark:text-indigo-400">Permohonan Affiliate Baru</h3>
                  <p className="text-xs text-indigo-700/80 dark:text-indigo-400 mb-3">{affiliates.filter(a => a.status === 'pending_access').length} permohonan menunggu kelulusan anda.</p>
                  <button onClick={() => bukaTab('affiliates')} className="px-4 py-1.5 bg-indigo-500 text-white text-xs font-bold rounded-lg hover:bg-indigo-600 transition-colors">Semak Permohonan</button>
                </div>
              </div>
            )}
            {payouts.length > 0 && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-2xl p-5 flex items-start gap-4">
                <span className="material-symbols-outlined text-amber-500 mt-0.5">warning</span>
                <div>
                  <h3 className="text-sm font-bold text-amber-800 dark:text-amber-400">Tindakan Diperlukan: Payout Tertangguh</h3>
                  <p className="text-xs text-amber-700/80 dark:text-amber-500 mb-3">Terdapat {payouts.length} permintaan payout yang perlukan pengesahan anda.</p>
                  <button onClick={() => bukaTab('payouts')} className="px-4 py-1.5 bg-amber-500 text-white text-xs font-bold rounded-lg hover:bg-amber-600 transition-colors">Urus Payout</button>
                </div>
              </div>
            )}
          </div>
          );
        })()}

        {activeTab === 'affiliates' && (
           <div className="space-y-4">
             {/* Search + Filter + Export */}
             <div className="flex items-center gap-3">
               <div className="relative flex-1">
                 <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[18px]">search</span>
                 <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Cari nama, email, kod rujukan, custom slug, atau no. WhatsApp..." className="w-full h-10 pl-10 pr-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm focus:ring-2 focus:ring-indigo-500/30 outline-none" />
               </div>
               <select value={groupFilter} onChange={e => setGroupFilter(e.target.value)} title="Tapis ikut partner group" className="h-10 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs font-bold text-slate-600 dark:text-slate-300 focus:ring-2 focus:ring-indigo-500/30 outline-none cursor-pointer whitespace-nowrap">
                 <option value="all">Semua Group</option>
                 {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                 <option value="none">Tiada Group</option>
               </select>
               <label className={`flex items-center gap-2 px-3 h-10 rounded-xl cursor-pointer text-xs font-bold transition-colors whitespace-nowrap ${activeOnly ? 'bg-emerald-500 text-white hover:bg-emerald-600' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'}`}>
                 <input type="checkbox" checked={activeOnly} onChange={e => setActiveOnly(e.target.checked)} className="w-4 h-4 accent-emerald-500" />
                 Yang Buat Sales Sahaja
               </label>
               {/* Susun — kawalan yang sama rupa dengan /workspace. Klik pada
                   tajuk lajur di bawah masih berfungsi dan kekal selaras kerana
                   kedua-duanya menulis `sortBy`/`sortDir` yang sama. */}
               <SortMenuAffiliate
                 jenis={sortBy}
                 arah={sortDir}
                 onTukar={(j, a) => { setSortBy(j); setSortDir(a); }}
               />
               <button onClick={() => handleExportCSV('affiliates')} className="flex items-center gap-1.5 px-4 h-10 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-xs font-bold rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors whitespace-nowrap">
                 <span className="material-symbols-outlined text-[16px]">download</span> CSV
               </button>
             </div>
             <div className="bg-white dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden">
               <div className="overflow-x-auto">
                 <table className="w-full text-left">
                   <thead>
                     <tr className="text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100 dark:border-slate-800">
                       {(() => {
                         const SortTh = ({ field, label, align = 'left' }) => {
                           const isActive = sortBy === field;
                           const onClick = () => {
                             if (isActive) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
                             else { setSortBy(field); setSortDir(field === 'name' ? 'asc' : 'desc'); }
                           };
                           const alignClass = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : '';
                           return (
                             <th onClick={onClick} className={`px-5 py-3 cursor-pointer select-none hover:text-slate-600 dark:hover:text-slate-200 transition-colors ${alignClass}`}>
                               <span className={isActive ? 'text-indigo-600 dark:text-indigo-400' : ''}>
                                 {label}{isActive && <span className="ml-1">{sortDir === 'asc' ? '↑' : '↓'}</span>}
                               </span>
                             </th>
                           );
                         };
                         return (
                           <>
                             <SortTh field="name" label="Nama / Email" />
                             <th className="px-5 py-3">Kod Rujukan</th>
                             <th className="px-5 py-3">WhatsApp</th>
                             <SortTh field="totalClicks" label="Klik" align="right" />
                             <SortTh field="totalReferrals" label="Rujukan" align="right" />
                             <SortTh field="paidCount" label="Paid" align="right" />
                             <SortTh field="conversionRate" label="Conv %" align="right" />
                             <SortTh field="lastSaleAt" label="Last Sale" align="right" />
                             <SortTh field="totalEarnings" label="Pendapatan" align="right" />
                             <SortTh field="overrideEarnings" label="Override T2" align="right" />
                             <SortTh field="unpaidBalance" label="Baki" align="right" />
                             <th className="px-5 py-3 text-center">Status</th>
                             <th className="px-5 py-3 text-center">Tindakan</th>
                           </>
                         );
                       })()}
                     </tr>
                   </thead>
                   <tbody>
                     {filteredAffiliates.map(aff => (
                       <React.Fragment key={aff.id}>
                         <tr id={`aff-row-${aff.id}`} className={`border-b border-slate-50 dark:border-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-800/30 cursor-pointer transition-colors ${highlightedAffiliateId === aff.id ? 'bg-purple-100 dark:bg-purple-900/30 ring-2 ring-purple-400 dark:ring-purple-500' : ''}`} onClick={() => setExpandedAffiliate(expandedAffiliate === aff.id ? null : aff.id)}>
                           <td className="px-5 py-3">
                             <p className="text-sm font-bold text-slate-700 dark:text-slate-200">{aff.name}</p>
                             <p className="text-xs text-slate-500">{aff.email}</p>
                           </td>
                           <td className="px-5 py-3"><span className="text-xs font-mono bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 px-2 py-1 rounded">{aff.refCode}</span></td>
                           <td className="px-5 py-3" onClick={e => e.stopPropagation()}>
                             {aff.whatsappNumber ? (
                               <a
                                 href={toWaLink(aff.whatsappNumber)}
                                 target="_blank"
                                 rel="noopener noreferrer"
                                 title="Buka WhatsApp (untuk approve masuk group)"
                                 className="inline-flex items-center gap-1.5 text-xs font-mono font-semibold text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300 hover:underline"
                               >
                                 <span className="material-symbols-outlined text-[14px]">chat</span>
                                 {aff.whatsappNumber}
                               </a>
                             ) : (
                               <span className="text-xs text-slate-300 dark:text-slate-600">—</span>
                             )}
                           </td>
                           <td className="px-5 py-3 text-right text-sm text-slate-600 dark:text-slate-400">{aff.totalClicks || 0}</td>
                           <td className="px-5 py-3 text-right text-sm text-slate-600 dark:text-slate-400">{aff.totalReferrals || 0}</td>
                           {(() => {
                             const stats = affiliateStats.get(aff.id) || { paidCount: 0, lastSaleAt: 0 };
                             const refs = aff.totalReferrals || 0;
                             const pct = refs > 0 ? (stats.paidCount / refs) * 100 : 0;
                             const pctColor = refs === 0 ? 'text-slate-300 dark:text-slate-600' : pct >= 10 ? 'text-emerald-600' : pct >= 5 ? 'text-amber-500' : 'text-slate-500';
                             return (
                               <>
                                 <td className="px-5 py-3 text-right">
                                   {stats.paidCount > 0
                                     ? <span className="text-sm font-bold text-emerald-600">{stats.paidCount}</span>
                                     : <span className="text-sm text-slate-400">0</span>}
                                 </td>
                                 <td className="px-5 py-3 text-right">
                                   {refs === 0
                                     ? <span className="text-sm text-slate-300 dark:text-slate-600">—</span>
                                     : <span className={`text-sm font-bold ${pctColor}`}>{pct.toFixed(1)}%</span>}
                                 </td>
                                 <td className="px-5 py-3 text-right">
                                   <span className={`text-xs font-bold ${lastSaleColor(stats.lastSaleAt)}`}>{formatRelativeTime(stats.lastSaleAt)}</span>
                                 </td>
                               </>
                             );
                           })()}
                           <td className="px-5 py-3 text-right text-sm font-bold text-emerald-600">{formatRM(aff.totalEarnings || 0)}</td>
                           <td className="px-5 py-3 text-right text-sm font-bold text-purple-600">
                             {aff.overrideEarnings ? formatRM(aff.overrideEarnings) : <span className="text-slate-300 dark:text-slate-600">—</span>}
                           </td>
                           <td className="px-5 py-3 text-right text-sm font-bold text-amber-500">
                             {aff.unpaidBalance < 0 ? (
                               <span className="text-red-500">{formatRM(aff.unpaidBalance || 0)}</span>
                             ) : formatRM(aff.unpaidBalance || 0)}
                             {aff.pendingAdjustment && (
                               <span className="ml-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-600" title={aff.pendingAdjustment.reason}>
                                 adj -RM{(aff.pendingAdjustment.amount / 100).toFixed(2)}
                               </span>
                             )}
                           </td>
                           <td className="px-5 py-3 text-center">
                             {aff.status === 'pending_access' ? (
                               <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600">Pending</span>
                             ) : aff.status === 'active' ? (
                               <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600">Aktif</span>
                             ) : (
                               <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-red-50 dark:bg-red-900/30 text-red-600">Digantung</span>
                             )}
                           </td>
                           <td className="px-5 py-3 text-center" onClick={e => e.stopPropagation()}>
                             <div className="flex items-center justify-center gap-1">
                               {aff.status === 'pending_access' ? (
                                 <>
                                   <button onClick={() => handleApproveAccess(aff)} className="px-2.5 py-1 bg-indigo-500 text-white text-[10px] font-bold rounded-lg hover:bg-indigo-600 transition-colors" title="Luluskan akses">Luluskan</button>
                                   <button onClick={() => handleRejectAccess(aff)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" title="Tolak permohonan">
                                     <span className="material-symbols-outlined text-[16px]">close</span>
                                   </button>
                                 </>
                               ) : (
                                 <>
                                   <button onClick={() => handleApproveAllCommissions(aff)} className="p-1.5 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors" title="Luluskan semua komisen pending">
                                     <span className="material-symbols-outlined text-[16px]">task_alt</span>
                                   </button>
                                   <button onClick={() => handleToggleAffiliateStatus(aff)} className="p-1.5 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors" title={aff.status === 'active' ? 'Gantung Affiliate' : 'Aktifkan Affiliate'}>
                                     <span className="material-symbols-outlined text-[16px]">{aff.status === 'active' ? 'block' : 'check_circle'}</span>
                                   </button>
                                 </>
                               )}
                             </div>
                           </td>
                         </tr>
                         {expandedAffiliate === aff.id && (
                           <tr><td colSpan={13} className="px-5 py-4 bg-slate-50/50 dark:bg-slate-800/20 border-b border-slate-200 dark:border-slate-800">
                             <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                               <div><p className="font-bold text-slate-400 uppercase tracking-wider mb-1">ID</p><p className="font-mono text-slate-600 dark:text-slate-300 break-all">{aff.id}</p></div>
                               <div>
                                 <p className="font-bold text-slate-400 uppercase tracking-wider mb-1">WhatsApp</p>
                                 {aff.whatsappNumber ? (
                                   <a href={toWaLink(aff.whatsappNumber)} target="_blank" rel="noopener noreferrer" className="font-mono text-emerald-600 hover:underline">
                                     {aff.whatsappNumber}
                                   </a>
                                 ) : <p className="text-slate-400">—</p>}
                               </div>
                               <div>
                                 <p className="font-bold text-slate-400 uppercase tracking-wider mb-1">Partner Group</p>
                                 <select
                                   value={aff.partnerGroupId || ''}
                                   onChange={async (e) => {
                                     const newGroupId = e.target.value || null;
                                     await adminAssignPartnerGroup(aff.id, newGroupId);
                                     setAffiliates(prev => prev.map(a => a.id === aff.id ? { ...a, partnerGroupId: newGroupId } : a));
                                   }}
                                   className="text-xs text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded px-2 py-1 cursor-pointer"
                                 >
                                   <option value="">Tiada</option>
                                   {groups.map(g => (
                                     <option key={g.id} value={g.id}>{g.name}</option>
                                   ))}
                                 </select>
                               </div>
                               <div><p className="font-bold text-slate-400 uppercase tracking-wider mb-1">Bank</p><p className="text-slate-600 dark:text-slate-300">{aff.payoutDetails?.bankName || '—'}</p><p className="font-mono text-slate-500">{aff.payoutDetails?.accountNumber || '—'}</p><p className="text-slate-500">{aff.payoutDetails?.accountHolder || '—'}</p></div>
                               <div><p className="font-bold text-slate-400 uppercase tracking-wider mb-1">Conversion Rate</p><p className="text-slate-600 dark:text-slate-300 font-bold">{aff.totalClicks > 0 ? ((aff.totalReferrals || 0) / aff.totalClicks * 100).toFixed(1) : '0.0'}%</p></div>
                               <div><p className="font-bold text-slate-400 uppercase tracking-wider mb-1">Tarikh Sertai</p><p className="text-slate-600 dark:text-slate-300">{formatDate(aff.joinedAt)}</p></div>
                               {aff.suspendedAt && <div><p className="font-bold text-red-400 uppercase tracking-wider mb-1">Digantung Pada</p><p className="text-red-600">{formatDate(aff.suspendedAt)}</p></div>}
                               <div><p className="font-bold text-slate-400 uppercase tracking-wider mb-1">Link Affiliate</p><p className="font-mono text-indigo-500 text-[10px] break-all">{window.location.origin}/r/{aff.refCode}</p></div>
                             </div>
                             {/* History Payout ejen — data dari cached active+history, filter by affiliateId */}
                             {(() => {
                               const affPayouts = [...payouts, ...payoutHistory]
                                 .filter(p => p.affiliateId === aff.id)
                                 .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
                               return (
                                 <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                                   <p className="font-bold text-slate-400 uppercase tracking-wider mb-2 text-[10px]">History Payout ({affPayouts.length})</p>
                                   {affPayouts.length === 0 ? (
                                     <p className="text-xs text-slate-400 italic">Ejen ini belum pernah request payout.</p>
                                   ) : (
                                     <div className="space-y-1.5">
                                       {affPayouts.slice(0, 10).map(p => (
                                         <div key={p.id} className="flex flex-wrap items-center gap-3 text-xs">
                                           <span className="text-slate-500 dark:text-slate-400 w-44">{formatDate(p.createdAt)}</span>
                                           <span className="font-bold text-slate-700 dark:text-slate-200 w-20">{formatRM(p.amount)}</span>
                                           <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full capitalize ${
                                             p.status === 'completed' ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600' :
                                             p.status === 'failed' ? 'bg-rose-50 dark:bg-rose-900/20 text-rose-600' :
                                             p.status === 'processing' ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600' :
                                             'bg-amber-50 dark:bg-amber-900/20 text-amber-600'
                                           }`}>{p.status}</span>
                                           <span className="text-slate-400 text-[10px] capitalize">{p.method?.replace('_', ' ') || 'bank transfer'}</span>
                                         </div>
                                       ))}
                                       {affPayouts.length > 10 && <p className="text-[10px] text-slate-400 italic mt-1">+{affPayouts.length - 10} lagi — lihat tab Payouts untuk senarai penuh</p>}
                                     </div>
                                   )}
                                 </div>
                               );
                             })()}
                           </td></tr>
                         )}
                       </React.Fragment>
                     ))}
                     {filteredAffiliates.length === 0 && <tr><td colSpan={13} className="py-8 text-center text-sm text-slate-400">{searchQuery ? 'Tiada hasil carian.' : (activeOnly ? 'Tiada ejen yang dah buat sales.' : 'Tiada affiliate.')}</td></tr>}
                   </tbody>
                 </table>
               </div>
             </div>
           </div>
        )}

        {activeTab === 'payouts' && (
          <div className="space-y-6">
            <div className="bg-white dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800">
                <h3 className="text-sm font-bold text-slate-700 dark:text-slate-200">Permintaan Payout Aktif (Pending &amp; Processing)</h3>
              </div>
              {payouts.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-8">Tiada permintaan payout pada masa ini.</p>
              ) : (
                <table className="w-full text-left">
                  <thead>
                    <tr className="text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100 dark:border-slate-800">
                      <th className="px-5 py-3">Affiliate</th>
                      <th className="px-5 py-3 text-right">Jumlah</th>
                      <th className="px-5 py-3">Bank Details</th>
                      <th className="px-5 py-3">Kaedah / Status</th>
                      <th className="px-5 py-3">Tarikh</th>
                      <th className="px-5 py-3 text-right">Tindakan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payouts.map(p => {
                      const aff = resolvePayoutAff(p);
                      return (
                      <tr key={p.id} className="border-b border-slate-50 dark:border-slate-800/50">
                        <td className="px-5 py-3">
                          <p className="text-sm font-bold text-slate-700 dark:text-slate-200">{aff.name || '— Tiada nama —'}</p>
                          {aff.email && <p className="text-xs text-slate-500">{aff.email}</p>}
                          {aff.refCode && <p className="text-[10px] font-mono text-slate-400 mt-0.5">{aff.refCode}</p>}
                          <p className="text-[10px] font-mono text-slate-300 dark:text-slate-600 mt-0.5" title={p.affiliateId}>{p.affiliateId.slice(0, 12)}…</p>
                        </td>
                        <td className="px-5 py-3 text-right text-sm font-bold text-amber-500">{formatRM(p.amount)}</td>
                        <td className="px-5 py-3">
                          <p className="text-xs font-bold text-slate-700 dark:text-slate-200">{p.bankDetails?.bankName || 'N/A'}</p>
                          <p className="text-xs text-slate-500">{p.bankDetails?.accountNumber}</p>
                          <p className="text-[10px] text-slate-400">{p.bankDetails?.accountHolder}</p>
                        </td>
                        <td className="px-5 py-3">
                           <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 capitalize block w-max">{p.method?.replace('_', ' ')}</span>
                           <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 mt-1 block w-max">{p.status}</span>
                        </td>
                        <td className="px-5 py-3 text-xs text-slate-500">{formatDate(p.createdAt)}</td>
                        <td className="px-5 py-3 text-right">
                          <button onClick={() => setEditingPayout(p)} className="px-3 py-1.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 text-xs font-bold rounded hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors">Tindakan</button>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Payout History (Completed + Failed) */}
            <div className="bg-white dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
                <h3 className="text-sm font-bold text-slate-700 dark:text-slate-200">History Payout (Completed &amp; Failed)</h3>
                <span className="text-[10px] font-bold text-slate-400 uppercase">{payoutHistory.length} rekod terakhir</span>
              </div>
              {payoutHistory.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-8">Tiada history payout lagi.</p>
              ) : (
                <table className="w-full text-left">
                  <thead>
                    <tr className="text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100 dark:border-slate-800">
                      <th className="px-5 py-3">Affiliate</th>
                      <th className="px-5 py-3 text-right">Jumlah</th>
                      <th className="px-5 py-3">Bank Details</th>
                      <th className="px-5 py-3">Kaedah / Status</th>
                      <th className="px-5 py-3">Tarikh</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payoutHistory.map(p => {
                      const aff = resolvePayoutAff(p);
                      return (
                      <tr key={p.id} className="border-b border-slate-50 dark:border-slate-800/50">
                        <td className="px-5 py-3">
                          <p className="text-sm font-bold text-slate-700 dark:text-slate-200">{aff.name || '— Tiada nama —'}</p>
                          {aff.email && <p className="text-xs text-slate-500">{aff.email}</p>}
                          {aff.refCode && <p className="text-[10px] font-mono text-slate-400 mt-0.5">{aff.refCode}</p>}
                          <p className="text-[10px] font-mono text-slate-300 dark:text-slate-600 mt-0.5" title={p.affiliateId}>{p.affiliateId.slice(0, 12)}…</p>
                        </td>
                        <td className="px-5 py-3 text-right text-sm font-bold text-slate-700 dark:text-slate-200">{formatRM(p.amount)}</td>
                        <td className="px-5 py-3">
                          <p className="text-xs font-bold text-slate-700 dark:text-slate-200">{p.bankDetails?.bankName || 'N/A'}</p>
                          <p className="text-xs text-slate-500">{p.bankDetails?.accountNumber}</p>
                          <p className="text-[10px] text-slate-400">{p.bankDetails?.accountHolder}</p>
                        </td>
                        <td className="px-5 py-3">
                           <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 capitalize block w-max">{p.method?.replace('_', ' ')}</span>
                           <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full mt-1 block w-max ${p.status === 'completed' ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600' : 'bg-rose-50 dark:bg-rose-900/20 text-rose-600'}`}>{p.status}</span>
                        </td>
                        <td className="px-5 py-3 text-xs text-slate-500">{formatDate(p.createdAt)}</td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Editing Payout Modal */}
            {editingPayout && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
                <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 w-full max-w-md shadow-2xl border border-slate-200 dark:border-slate-800">
                  <h3 className="text-lg font-bold text-slate-800 dark:text-white mb-4">Pengurusan Payout</h3>
                  
                  <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 mb-4 border border-slate-100 dark:border-slate-700">
                    {(() => {
                      const aff = resolvePayoutAff(editingPayout);
                      return (
                        <div className="mb-3 pb-3 border-b border-slate-200 dark:border-slate-700">
                          <p className="text-sm font-bold text-slate-800 dark:text-white">{aff.name || '— Tiada nama —'}</p>
                          {aff.email && <p className="text-xs text-slate-500">{aff.email}</p>}
                          {aff.refCode && <p className="text-[10px] font-mono text-slate-400 mt-0.5">Ref: {aff.refCode}</p>}
                        </div>
                      );
                    })()}
                    <div className="flex justify-between mb-2"><span className="text-xs text-slate-400">Payout ID:</span> <span className="text-xs font-mono">{editingPayout.id}</span></div>
                    <div className="flex justify-between mb-2"><span className="text-xs text-slate-400">Affiliate UID:</span> <span className="text-xs font-mono text-slate-500" title={editingPayout.affiliateId}>{editingPayout.affiliateId.slice(0, 16)}…</span></div>
                    <div className="flex justify-between mb-2"><span className="text-xs text-slate-400">Jumlah:</span> <span className="text-sm font-bold text-amber-500">{formatRM(editingPayout.amount)}</span></div>
                    <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                      <p className="text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">Cawangan Bank:</p>
                      <p className="text-sm text-slate-800 dark:text-white">{editingPayout.bankDetails?.bankName}</p>
                      <p className="text-sm text-slate-800 dark:text-white font-mono my-1">{editingPayout.bankDetails?.accountNumber}</p>
                      <p className="text-xs text-slate-500 uppercase">{editingPayout.bankDetails?.accountHolder}</p>
                    </div>
                  </div>

                  <div className="mb-4">
                     <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Nota Penolakan (Jika Ada)</label>
                     <textarea
                       value={payoutNotes}
                       onChange={e => setPayoutNotes(e.target.value)}
                       placeholder="Sebab ditolak..."
                       className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm p-2 text-slate-800 dark:text-white focus:ring-2 focus:ring-indigo-500/20 outline-none"
                       rows="2"
                     ></textarea>
                  </div>

                  <div className="flex flex-col gap-2">
                    {editingPayout.status === 'pending' && (
                      <button onClick={() => handleApprovePayout(editingPayout.id)} className="w-full py-2 bg-blue-500 text-white font-bold rounded-lg hover:bg-blue-600 transition-colors">1. Luluskan untuk Proses (Processing)</button>
                    )}
                    {editingPayout.status === 'processing' && (
                      <button onClick={() => handleCompletePayout(editingPayout)} className="w-full py-2 bg-emerald-500 text-white font-bold rounded-lg hover:bg-emerald-600 transition-colors">2. Sahkan Bayaran Selesai (Completed)</button>
                    )}
                    {(editingPayout.status === 'pending' || editingPayout.status === 'processing') && (
                      <button onClick={() => handleRejectPayout(editingPayout)} className="w-full py-2 bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400 font-bold rounded-lg hover:bg-rose-100 dark:hover:bg-rose-900/30 transition-colors">Tolak Payout / Batal (Failed)</button>
                    )}
                    {(editingPayout.status === 'completed' || editingPayout.status === 'failed') && (
                      <p className="text-xs text-center text-slate-400 py-2">Payout ini sudah <span className="font-bold capitalize">{editingPayout.status}</span> — tiada tindakan lagi.</p>
                    )}
                    <button onClick={() => setEditingPayout(null)} className="w-full py-2 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-bold rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 mt-2 transition-colors">Tutup</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'commissions' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-500">Jumlah: <span className="font-bold text-slate-700 dark:text-slate-200">{allCommissions.length}</span> &nbsp;|&nbsp; Pending: <span className="font-bold text-amber-500">{pendingCommissions.length}</span></p>
              <button onClick={() => handleExportCSV('commissions')} className="flex items-center gap-1.5 px-4 h-9 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-xs font-bold rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors whitespace-nowrap">
                <span className="material-symbols-outlined text-[16px]">download</span> CSV
              </button>
            </div>
            <div className="bg-white dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden">
              <table className="w-full text-left">
                <thead>
                  <tr className="text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100 dark:border-slate-800">
                    <th className="px-5 py-3">Affiliate</th>
                    <th className="px-5 py-3">Customer</th>
                    <th className="px-5 py-3 text-center">Jenis</th>
                    <th className="px-5 py-3">Plan</th>
                    <th className="px-5 py-3 text-right">Jumlah</th>
                    <th className="px-5 py-3 text-right">Kadar</th>
                    <th className="px-5 py-3 text-center">Status</th>
                    <th className="px-5 py-3">Tarikh</th>
                    <th className="px-5 py-3 text-center">Tindakan</th>
                  </tr>
                </thead>
                <tbody>
                  {allCommissions.map(c => {
                    const aff = affiliates.find(a => a.id === c.affiliateId);
                    const txn = transactionMap.get(c.referralId);
                    const jenis = resolveCommissionPurchaseType(c); // BARU: fallback lookup
                    return (
                      <tr key={c.id} className="border-b border-slate-50 dark:border-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-800/30">
                        <td className="px-5 py-3">
                          <p className="text-sm font-bold text-slate-700 dark:text-slate-200">{aff?.name || '—'}</p>
                          <p className="text-[10px] text-slate-400 font-mono">{c.affiliateId?.slice(0, 12)}...</p>
                        </td>
                        <td className="px-5 py-3">
                          <p className="text-sm text-slate-700 dark:text-slate-200 truncate max-w-[200px]">{txn?.customerEmail || '—'}</p>
                          {txn?.customerName && <p className="text-[10px] text-slate-400">{txn.customerName}</p>}
                        </td>
                        <td className="px-5 py-3 text-center">
                          {jenis === 'renewal' ? (
                            <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-[10px] font-bold whitespace-nowrap">
                              🔄 Renewal
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-[10px] font-bold whitespace-nowrap">
                              🆕 Baru
                            </span>
                          )}
                        </td>
                        <td className="px-5 py-3 text-xs text-slate-600 dark:text-slate-400 capitalize">{c.plan || '—'}</td>
                        <td className="px-5 py-3 text-right text-sm font-bold text-emerald-600">{formatRM(c.amount || 0)}</td>
                        <td className="px-5 py-3 text-right text-xs text-slate-500">{c.rate ? `${(c.rate * 100).toFixed(0)}%` : '—'}</td>
                        <td className="px-5 py-3 text-center">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${c.status === 'pending' ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-600' : c.status === 'approved' ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600' : 'bg-slate-100 dark:bg-slate-800 text-slate-500'}`}>{c.status}</span>
                        </td>
                        <td className="px-5 py-3 text-xs text-slate-500">{formatDate(c.createdAt)}</td>
                        <td className="px-5 py-3 text-center">
                          {c.status === 'pending' ? (
                            <button onClick={() => handleApproveOneCommission(c.id)} className="px-3 py-1 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 text-[10px] font-bold rounded-lg hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-colors">Luluskan</button>
                          ) : <span className="text-[10px] text-slate-400">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                  {allCommissions.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-sm text-slate-400">Tiada rekod komisen.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'referrals' && (
          <div className="space-y-4">
            {/* Summary stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="bg-white dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-xl px-4 py-3">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Jumlah Rujukan</p>
                <p className="text-2xl font-black text-slate-800 dark:text-white mt-0.5">{referralRows.length}</p>
              </div>
              <div className="bg-white dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-xl px-4 py-3">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Belum Convert</p>
                <p className="text-2xl font-black text-amber-500 mt-0.5">{pendingReferralsCount}</p>
              </div>
              <div className="bg-white dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-xl px-4 py-3">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Dah Convert {referralRows.length > 0 && <span className="text-slate-300">· {((convertedReferralsCount / referralRows.length) * 100).toFixed(1)}%</span>}</p>
                <p className="text-2xl font-black text-emerald-500 mt-0.5">{convertedReferralsCount}</p>
              </div>
            </div>

            {/* Filter + search + CSV */}
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1 bg-white dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-xl p-1">
                {[
                  { id: 'all', label: 'Semua' },
                  { id: 'pending', label: 'Belum Convert' },
                  { id: 'converted', label: 'Dah Convert' }
                ].map(f => (
                  <button
                    key={f.id}
                    onClick={() => setReferralFilter(f.id)}
                    className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${referralFilter === f.id ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              {/* BARU: Filter Jenis */}
              <div className="flex items-center gap-1 bg-white dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-xl p-1">
                {[
                  { id: 'all', label: 'Semua Jenis' },
                  { id: 'new', label: '🆕 Baru' },
                  { id: 'renewal', label: '🔄 Renewal' }
                ].map(f => (
                  <button
                    key={f.id}
                    onClick={() => setReferralJenisFilter(f.id)}
                    className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${referralJenisFilter === f.id ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <input
                type="text"
                value={referralSearch}
                onChange={e => setReferralSearch(e.target.value)}
                placeholder="Cari email, no WhatsApp, ejen, atau ref code..."
                className="flex-1 min-w-[240px] bg-white dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-xl px-3 py-2 text-sm text-slate-700 dark:text-slate-200 placeholder-slate-400 focus:ring-2 focus:ring-indigo-500/30 outline-none"
              />
              <button onClick={() => handleExportCSV('referrals')} className="flex items-center gap-1.5 px-4 h-10 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-xs font-bold rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors whitespace-nowrap">
                <span className="material-symbols-outlined text-[16px]">download</span> CSV
              </button>
            </div>

            {/* Table */}
            <div className="bg-white dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden">
              <table className="w-full text-left">
                <thead>
                  <tr className="text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100 dark:border-slate-800">
                    <th className="px-5 py-3">User Baru</th>
                    <th className="px-5 py-3">WhatsApp</th>
                    <th className="px-5 py-3">Ejen Rujukan</th>
                    <th className="px-5 py-3 text-center">Status</th>
                    <th className="px-5 py-3 text-center">Jenis</th>
                    <th className="px-5 py-3">Tarikh Daftar</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const q = referralSearch.toLowerCase().trim();
                    const qDigits = referralSearch.replace(/\D/g, ''); // angka sahaja — padanan no. WhatsApp abai-format
                    const filtered = referralRows.filter(r => {
                      const converted = isReferralConverted(r);
                      if (referralFilter === 'pending' && converted) return false;
                      if (referralFilter === 'converted' && !converted) return false;
                      // BARU: filter Jenis
                      const jenis = r.purchaseType === 'renewal' ? 'renewal' : 'new';
                      if (referralJenisFilter !== 'all' && referralJenisFilter !== jenis) return false;
                      if (!q) return true;
                      return (r.refereeEmail || '').toLowerCase().includes(q)
                        || (r.referrerEmail || '').toLowerCase().includes(q)
                        || (r.referrerName || '').toLowerCase().includes(q)
                        || (r.refCode || '').toLowerCase().includes(q)
                        || (r.whatsappNumber || '').toLowerCase().includes(q)
                        || (r.refereeName || '').toLowerCase().includes(q)
                        || (qDigits.length >= 3 && (r.whatsappNumber || '').replace(/\D/g, '').includes(qDigits));
                    });
                    if (filtered.length === 0) {
                      return <tr><td colSpan={6} className="py-8 text-center text-sm text-slate-400">Tiada rekod rujukan.</td></tr>;
                    }
                    return filtered.map(r => {
                      const wa = r.whatsappNumber;
                      const converted = isReferralConverted(r);
                      return (
                        <tr key={r.id} className="border-b border-slate-50 dark:border-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-800/30">
                          <td className="px-5 py-3">
                            <p className="text-sm font-bold text-slate-700 dark:text-slate-200 truncate max-w-[240px]">{r.refereeName || r.refereeEmail?.split('@')[0] || '—'}</p>
                            <p className="text-[11px] text-slate-500 truncate max-w-[240px]">{r.refereeEmail || '—'}</p>
                          </td>
                          <td className="px-5 py-3">
                            {wa ? (
                              <a
                                href={toWaLink(wa)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs font-mono text-emerald-600 hover:underline"
                                title="Buka di WhatsApp"
                              >
                                <span className="material-symbols-outlined text-[14px]">chat</span>
                                {wa}
                              </a>
                            ) : (
                              <span className="text-[11px] text-slate-400">—</span>
                            )}
                          </td>
                          <td className="px-5 py-3">
                            <button
                              onClick={() => { bukaTab('affiliates'); setSearchQuery(r.refCode || ''); if (r.referrerId) { setExpandedAffiliate(r.referrerId); setHighlightedAffiliateId(r.referrerId); setTimeout(() => setHighlightedAffiliateId(null), 3000); } }}
                              className="text-left group"
                              disabled={!r.referrerId}
                            >
                              <p className={`text-sm font-bold text-slate-700 dark:text-slate-200 ${r.referrerId ? 'group-hover:text-indigo-600' : ''}`}>{r.referrerName || r.referrerEmail || '—'}</p>
                              <p className="text-[10px] text-indigo-500 font-mono">{r.refCode || '—'}</p>
                            </button>
                          </td>
                          <td className="px-5 py-3 text-center">
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${converted ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600' : 'bg-amber-50 dark:bg-amber-900/20 text-amber-600'}`}>
                              {converted ? 'Converted' : 'Pending'}
                            </span>
                          </td>
                          <td className="px-5 py-3 text-center">
                            {r.purchaseType === 'renewal' ? (
                              <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-[10px] font-bold whitespace-nowrap">
                                🔄 Renewal
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-[10px] font-bold whitespace-nowrap">
                                🆕 Baru
                              </span>
                            )}
                          </td>
                          <td className="px-5 py-3 text-xs text-slate-500">{formatDate(r.createdAt)}</td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'config' && (
          <div className="max-w-2xl bg-white dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-2xl p-6">
             <h3 className="text-sm font-bold text-slate-700 dark:text-slate-200 mb-4">Tetapan Sistem Affiliate</h3>
             {editableConfig ? (
               <div className="space-y-4">
                 <div>
                   <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Kadar Komisen Default</label>
                   <div className="flex items-center gap-2">
                     <input type="number" step="0.01" min="0" max="1" value={editableConfig.commissionRate} onChange={e => setEditableConfig(p => ({ ...p, commissionRate: parseFloat(e.target.value) || 0 }))} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-800 dark:text-white font-mono w-28 focus:ring-2 focus:ring-indigo-500/30 outline-none" />
                     <span className="text-sm text-slate-500">({(editableConfig.commissionRate * 100).toFixed(0)}%)</span>
                   </div>
                   <p className="text-[10px] text-slate-400 mt-1">Masukkan dalam pecahan. Contoh: 0.25 = 25%</p>
                 </div>

                 <div>
                   <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Cookie Duration</label>
                   <div className="flex items-center gap-2">
                     <input type="number" min="1" max="365" value={editableConfig.cookieDuration} onChange={e => setEditableConfig(p => ({ ...p, cookieDuration: parseInt(e.target.value) || 30 }))} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-800 dark:text-white font-mono w-24 focus:ring-2 focus:ring-indigo-500/30 outline-none" />
                     <span className="text-sm text-slate-500">Hari</span>
                   </div>
                 </div>

                 <div>
                   <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Minimum Payout (sen)</label>
                   <div className="flex items-center gap-2">
                     <input type="number" min="100" step="100" value={editableConfig.minPayoutThreshold} onChange={e => setEditableConfig(p => ({ ...p, minPayoutThreshold: parseInt(e.target.value) || 5000 }))} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-800 dark:text-white font-mono w-32 focus:ring-2 focus:ring-indigo-500/30 outline-none" />
                     <span className="text-sm text-slate-500">= RM{(editableConfig.minPayoutThreshold / 100).toFixed(2)}</span>
                   </div>
                 </div>

                 <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
                   <button onClick={handleSaveConfig} disabled={savingConfig} className="px-6 py-2.5 bg-indigo-600 text-white font-bold rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 text-sm">
                     {savingConfig ? 'Menyimpan...' : 'Simpan Tetapan'}
                   </button>
                 </div>
               </div>
             ) : (
               <p className="text-sm text-slate-500">Memuat turun tetapan...</p>
             )}
          </div>
        )}

        {/* Partner Groups → klik nama group → listing basic ejen dalam group (page kecil, tab sama) */}
        {activeTab === 'groups' && viewingGroupId && (() => {
          const grp = groups.find(g => g.id === viewingGroupId);
          const members = affiliates.filter(a => a.partnerGroupId === viewingGroupId);
          return (
            <div className="bg-white dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center gap-3">
                <button onClick={() => setViewingGroupId(null)} className="flex items-center gap-1 text-xs font-bold text-slate-500 hover:text-indigo-600 transition-colors">
                  <span className="material-symbols-outlined text-[18px]">arrow_back</span> Kembali
                </button>
                <h3 className="text-sm font-bold text-slate-700 dark:text-slate-200">Ejen dalam Group: {grp?.name || '—'}</h3>
                <span className="text-xs text-slate-400">({members.length})</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100 dark:border-slate-800">
                      <th className="px-5 py-3">Nama / Email</th>
                      <th className="px-5 py-3">Kod Rujukan</th>
                      <th className="px-5 py-3 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.length === 0 && <tr><td colSpan={3} className="py-8 text-center text-sm text-slate-400">Tiada ejen dalam group ini.</td></tr>}
                    {members.map(a => (
                      <tr key={a.id} className="border-b border-slate-50 dark:border-slate-800/50">
                        <td className="px-5 py-3"><p className="text-sm font-bold text-slate-700 dark:text-slate-200">{a.name}</p><p className="text-xs text-slate-500">{a.email}</p></td>
                        <td className="px-5 py-3"><span className="text-xs font-mono bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 px-2 py-1 rounded">{a.refCode}</span></td>
                        <td className="px-5 py-3 text-center">
                          {a.status === 'pending_access'
                            ? <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600">Pending</span>
                            : a.status === 'active'
                              ? <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600">Aktif</span>
                              : <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-red-50 dark:bg-red-900/30 text-red-600">Digantung</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}

        {activeTab === 'groups' && !viewingGroupId && (
           <div className="bg-white dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden">
             <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
               <h3 className="text-sm font-bold text-slate-700 dark:text-slate-200">Partner Groups</h3>
               <button
                 onClick={() => setEditingGroup({ name: '', description: '', commissionRate: 0.25, tier2Rate: 0, isDefault: false })}
                 className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-500 text-white text-xs font-bold rounded-lg hover:bg-indigo-600 transition-colors"
               >
                 <span className="material-symbols-outlined text-[16px]">add</span> Tambah Group
               </button>
             </div>
             <table className="w-full text-left">
               <thead>
                 <tr className="text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100 dark:border-slate-800">
                   <th className="px-5 py-3">Nama</th>
                   <th className="px-5 py-3">Penerangan</th>
                   <th className="px-5 py-3 text-right">Kadar Komisen</th>
                   <th className="px-5 py-3 text-right">Override T2</th>
                   <th className="px-5 py-3 text-right">Ahli</th>
                   <th className="px-5 py-3 text-center">Tindakan</th>
                 </tr>
               </thead>
               <tbody>
                 {groups.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-sm text-slate-400">Tiada partner group.</td></tr>}
                 {groups.map(g => (
                   <tr key={g.id} className="border-b border-slate-50 dark:border-slate-800/50">
                     <td className="px-5 py-3 text-sm font-bold">
                       <button onClick={() => setViewingGroupId(g.id)} className="text-slate-700 dark:text-slate-200 hover:text-indigo-600 dark:hover:text-indigo-400 hover:underline text-left transition-colors" title="Lihat ejen dalam group ini">{g.name}</button>
                       {g.isDefault && <span className="text-[9px] bg-slate-100 dark:bg-slate-800 text-slate-500 py-0.5 px-1.5 rounded ml-2">Default</span>}
                     </td>
                     <td className="px-5 py-3 text-xs text-slate-500">{g.description}</td>
                     <td className="px-5 py-3 text-right text-sm font-bold text-indigo-500">{((g.commissionRate || 0) * 100).toFixed(0)}%</td>
                     <td className="px-5 py-3 text-right text-sm font-bold">
                       {g.tier2Rate > 0
                         ? <span className="text-purple-600">{(g.tier2Rate * 100).toFixed(0)}%</span>
                         : <span className="text-slate-300 dark:text-slate-600">—</span>}
                     </td>
                     <td className="px-5 py-3 text-right text-xs text-slate-500">{g.affiliateCount || 0}</td>
                     <td className="px-5 py-3 text-center">
                       <div className="flex items-center justify-center gap-1">
                         <button
                           onClick={() => setEditingGroup({
                             id: g.id,
                             name: g.name || '',
                             description: g.description || '',
                             commissionRate: g.commissionRate || 0,
                             tier2Rate: g.tier2Rate || 0,
                             isDefault: !!g.isDefault
                           })}
                           className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors"
                           title="Edit group"
                         >
                           <span className="material-symbols-outlined text-[16px]">edit</span>
                         </button>
                         <button
                           onClick={() => handleDeleteGroup(g)}
                           className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                           title="Padam group"
                         >
                           <span className="material-symbols-outlined text-[16px]">delete</span>
                         </button>
                       </div>
                     </td>
                   </tr>
                 ))}
               </tbody>
             </table>

             {/* Edit/Add Group Modal */}
             {editingGroup && (
               <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
                 <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 w-full max-w-md shadow-2xl border border-slate-200 dark:border-slate-800">
                   <h3 className="text-lg font-bold text-slate-800 dark:text-white mb-4">{editingGroup.id ? 'Edit Group' : 'Tambah Group Baru'}</h3>

                   <div className="space-y-3">
                     <div>
                       <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Nama</label>
                       <input type="text" value={editingGroup.name} onChange={e => setEditingGroup(p => ({ ...p, name: e.target.value }))} placeholder="cth: VIP Partners" className="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-800 dark:text-white focus:ring-2 focus:ring-indigo-500/30 outline-none" />
                     </div>
                     <div>
                       <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Penerangan</label>
                       <textarea value={editingGroup.description} onChange={e => setEditingGroup(p => ({ ...p, description: e.target.value }))} placeholder="Penerangan ringkas" rows="2" className="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-800 dark:text-white focus:ring-2 focus:ring-indigo-500/30 outline-none" />
                     </div>
                     <div>
                       <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Kadar Komisen</label>
                       <div className="flex items-center gap-2">
                         <input type="number" step="0.01" min="0" max="1" value={editingGroup.commissionRate} onChange={e => setEditingGroup(p => ({ ...p, commissionRate: parseFloat(e.target.value) || 0 }))} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-800 dark:text-white font-mono w-28 focus:ring-2 focus:ring-indigo-500/30 outline-none" />
                         <span className="text-sm text-slate-500">({((editingGroup.commissionRate || 0) * 100).toFixed(0)}%)</span>
                       </div>
                       <p className="text-[10px] text-slate-400 mt-1">Pecahan. Contoh: 0.4 = 40%</p>
                     </div>
                     <div>
                       <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Kadar Override Tier 2</label>
                       <div className="flex items-center gap-2">
                         <input type="number" step="0.01" min="0" max="1" value={editingGroup.tier2Rate ?? 0} onChange={e => setEditingGroup(p => ({ ...p, tier2Rate: parseFloat(e.target.value) || 0 }))} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-800 dark:text-white font-mono w-28 focus:ring-2 focus:ring-purple-500/30 outline-none" />
                         <span className="text-sm text-slate-500">({((editingGroup.tier2Rate || 0) * 100).toFixed(0)}%)</span>
                       </div>
                       <p className="text-[10px] text-slate-400 mt-1">Override untuk upline ejen (2-tier). <span className="font-bold">0 = group ini takda 2-tier.</span> Contoh: 0.05 = 5%</p>
                     </div>
                     <label className="flex items-center gap-2 cursor-pointer">
                       <input type="checkbox" checked={!!editingGroup.isDefault} onChange={e => setEditingGroup(p => ({ ...p, isDefault: e.target.checked }))} className="w-4 h-4 accent-indigo-500" />
                       <span className="text-xs text-slate-600 dark:text-slate-300">Set sebagai default group</span>
                     </label>
                   </div>

                   <div className="flex gap-2 mt-5">
                     <button onClick={handleSaveGroup} disabled={savingGroup} className="flex-1 py-2 bg-indigo-600 text-white font-bold rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 text-sm">
                       {savingGroup ? 'Menyimpan...' : 'Simpan'}
                     </button>
                     <button onClick={() => setEditingGroup(null)} className="flex-1 py-2 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-bold rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors text-sm">
                       Batal
                     </button>
                   </div>
                 </div>
               </div>
             )}
           </div>
        )}

        {activeTab === 'logs' && (
           <div className="bg-white dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden">
             <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800">
               <h3 className="text-sm font-bold text-slate-700 dark:text-slate-200">Audit Logs (50 Terkini)</h3>
             </div>
             <table className="w-full text-left">
               <thead>
                 <tr className="text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100 dark:border-slate-800">
                   <th className="px-5 py-3">Masa</th>
                   <th className="px-5 py-3">Tindakan</th>
                   <th className="px-5 py-3">Pelaku</th>
                   <th className="px-5 py-3">Target / Payload</th>
                 </tr>
               </thead>
               <tbody>
                 {auditLogs.length === 0 && <tr><td colSpan={4} className="py-8 text-center text-sm text-slate-400">Tiada rekod audit log.</td></tr>}
                 {auditLogs.map(log => (
                   <tr key={log.id} className="border-b border-slate-50 dark:border-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-800/20">
                     <td className="px-5 py-3 text-[10px] text-slate-400 whitespace-nowrap">{formatDate(log.createdAt)}</td>
                     <td className="px-5 py-3">
                       <span className="text-xs font-bold text-slate-700 dark:text-slate-300">{log.action}</span>
                     </td>
                     <td className="px-5 py-3 text-xs text-slate-500">{log.actorId} {log.actorEmail && `(${log.actorEmail})`}</td>
                     <td className="px-5 py-3">
                       <p className="text-[10px] text-slate-500 font-mono overflow-auto max-w-xs">{log.payload ? JSON.stringify(log.payload) : '—'}</p>
                     </td>
                   </tr>
                 ))}
               </tbody>
             </table>
           </div>
        )}
      </div>
    </div>
  );
};

export default AdminAffiliateView;
